import * as PIXI from 'pixi.js';
import type { Token } from '@machina/core';
import { IsoTokenRenderer } from './IsoTokenRenderer';
import { useTokenStore } from '../stores/tokenStore';

/**
 * IsoTokenLayer
 *
 * Manages isometric token renderers. Syncs with tokenStore just like TokenLayer.
 * Handles idle bob animation via Pixi ticker and depth sorting.
 */
export class IsoTokenLayer {
  public container: PIXI.Container;

  private renderers: Map<string, IsoTokenRenderer> = new Map();
  private tileWidth: number;
  private tileHeight: number;
  private unsubscribe: (() => void) | null = null;
  private ticker: PIXI.Ticker | null = null;
  private tickerFn: (() => void) | null = null;
  private lastTime = 0;

  constructor(tileWidth: number, tileHeight: number) {
    this.container = new PIXI.Container();
    this.container.sortableChildren = true;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;

    this.subscribeToStore();
  }

  /** Wire up the Pixi ticker for animations (bob + walk) */
  setTicker(ticker: PIXI.Ticker): void {
    this.ticker = ticker;
    this.lastTime = performance.now();

    this.tickerFn = () => {
      const now = performance.now();
      const dt = now - this.lastTime;
      this.lastTime = now;

      for (const renderer of this.renderers.values()) {
        renderer.applyBob(dt);
        renderer.tickWalk();
      }
    };

    ticker.add(this.tickerFn);
  }

  private subscribeToStore(): void {
    this.unsubscribe = useTokenStore.subscribe((state, prevState) => {
      if (state.tokens !== prevState.tokens || state.selectedIds !== prevState.selectedIds) {
        this.syncTokens(state.tokens, state.selectedIds);
      }
    });

    // Initial sync
    const { tokens, selectedIds } = useTokenStore.getState();
    this.syncTokens(tokens, selectedIds);
  }

  private syncTokens(tokens: Map<string, Token>, selectedIds: Set<string>): void {
    // Add/update
    for (const [id, token] of tokens) {
      const existing = this.renderers.get(id);
      // Inject selection state into token for the renderer
      const withSelection = { ...token, selected: selectedIds.has(id) };

      if (existing) {
        existing.update(withSelection);
      } else {
        this.addRenderer(withSelection);
      }
    }

    // Remove deleted
    for (const id of this.renderers.keys()) {
      if (!tokens.has(id)) {
        this.removeRenderer(id);
      }
    }
  }

  private addRenderer(token: Token): void {
    const renderer = new IsoTokenRenderer(token, this.tileWidth, this.tileHeight);

    renderer.onClick = (tokenId, shiftKey) => {
      useTokenStore.getState().selectToken(tokenId, shiftKey);
    };

    renderer.onDragEnd = (tokenId, x, y) => {
      useTokenStore.getState().moveToken(tokenId, { x, y }, true);
    };

    this.renderers.set(token.id, renderer);
    this.container.addChild(renderer.container);
  }

  private removeRenderer(id: string): void {
    const renderer = this.renderers.get(id);
    if (renderer) {
      this.container.removeChild(renderer.container);
      renderer.destroy();
      this.renderers.delete(id);
    }
  }

  /** Refresh torch label visibility on all renderers */
  refreshTorchLabels(): void {
    for (const renderer of this.renderers.values()) {
      renderer.refreshLabelVisibility();
    }
  }

  /** Get renderer by token id — used for click-to-move targeting */
  getRenderer(tokenId: string): IsoTokenRenderer | undefined {
    return this.renderers.get(tokenId);
  }

  destroy(): void {
    this.unsubscribe?.();
    if (this.ticker && this.tickerFn) {
      this.ticker.remove(this.tickerFn);
    }
    for (const renderer of this.renderers.values()) {
      renderer.destroy();
    }
    this.renderers.clear();
    this.container.destroy({ children: true });
  }
}
